/**
 * Console assistant: `/api/v1/assistant/…`.
 *
 * Status discovery returns only `{enabled:false}` to a live member when the
 * assistant is off. Operational endpoints and unavailable objects answer
 * 404; consumers treat them as absent and never retry them.
 *
 * History and widgets have a bounded memory cache, addressed by authenticated
 * principal. The shared QueryClient cancels and clears it on logout/switch.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import { CanceledError } from 'axios';
import useAuthStore, { selectPrincipal } from 'stores/authStore';
import { ASSISTANT_CACHE_MS, HISTORY_MAX_PAGES, HISTORY_PAGE_SIZE, boundAssistantCache, flattenHistory, mergeLatestHistory } from './assistantCache';

const BASE = '/api/v1/assistant';
export const ASSISTANT_AVAILABILITY_MS = 30 * 1000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether `value` has the shape of an assistant object id (a UUID). */
export function isAssistantId(value) {
  return typeof value === 'string' && UUID.test(value);
}

/**
 * One id as one path segment: never `/`, `?` or `#` into the API path, and
 * never a bare dot segment (`.`/`..`, which the browser resolves).
 */
const seg = (id) => {
  const value = String(id);
  return /^\.*$/.test(value) ? '-' : encodeURIComponent(value);
};

export const assistantKeys = {
  all: ['assistant'],
  status: ['assistant', 'status'],
  skills: ['assistant', 'skills'],
  settings: ['assistant', 'settings'],
  conversations: ['assistant', 'conversations'],
  conversation: (id) => ['assistant', 'conversation', id],
  diagnostics: (id) => ['assistant', 'diagnostics', id],
  details: (id) => ['assistant', 'details', id],
  activeRun: ['assistant', 'activeRun'],
  widget: (messageId, index, filters) =>
    filters && Object.keys(filters).length
      ? ['assistant', 'widget', messageId, index, filters]
      : ['assistant', 'widget', messageId, index],
};

/** Fixed error code from an assistant API error (`{"error": code}`), or ''. */
export function assistantErrorCode(err) {
  const code = err?.response?.data?.error;
  return typeof code === 'string' ? code : '';
}

/** A 404 from the assistant API: the feature (or the object) does not exist. */
export function isAssistantNotFound(err) {
  return err?.response?.status === 404;
}

/** Retry transient failures once; a 4xx is an answer, not a glitch. */
const retryTransient = (count, err) => {
  const status = err?.response?.status;
  if (status && status < 500) return false;
  return count < 1;
};
const currentPrincipal = () => ({ principal: selectPrincipal(useAuthStore.getState()) });
const isCurrentPrincipal = (context) => context?.principal === selectPrincipal(useAuthStore.getState());
const privateResult = (response, principal) => {
  if (principal !== selectPrincipal(useAuthStore.getState())) throw new CanceledError('Authentication context changed.');
  return response.data;
};

// ── Status and settings ─────────────────────────────────────────────────────

/**
 * Whether the assistant exists for this user; only `enabled:true` allows it. With
 * it: the connected provider's families, models with their efforts, and the
 * user's last pick (the composer's picker). The sidebar keeps this mounted on
 * every console page. Foreground polling and focus refresh keep rollout and
 * membership changes bounded; disabled discovery exposes no provider offer.
 */
export function useAssistantStatus() {
  return useQuery({
    queryKey: assistantKeys.status,
    queryFn: ({ signal }) => {
      const principal = selectPrincipal(useAuthStore.getState());
      return apiClient.get(`${BASE}/status/`, { signal, originPrincipal: principal }).then((r) => privateResult(r, principal));
    },
    retry: retryTransient,
    staleTime: ASSISTANT_AVAILABILITY_MS,
    refetchInterval: ASSISTANT_AVAILABILITY_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Dropped with the console (sign-out), never shown to the next principal.
    gcTime: 0,
  });
}

/** Only server-registered workflows are offered by the slash picker. */
export function useAssistantSkills({ enabled = true } = {}) {
  return useQuery({
    queryKey: assistantKeys.skills,
    queryFn: ({ signal }) => apiClient.get(`${BASE}/skills/`, { signal }).then((r) => r.data.skills),
    enabled,
    retry: retryTransient,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
}

export function useAssistantSettings({ enabled = true } = {}) {
  const principal = selectPrincipal(useAuthStore.getState());
  return useQuery({
    queryKey: assistantKeys.settings,
    queryFn: ({ signal }) => apiClient.get(`${BASE}/settings/`, { signal, originPrincipal: principal }).then((r) => privateResult(r, principal)),
    enabled,
    retry: retryTransient,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Provider consent and available models: `allowed_model_ids: null` inherits
 * the global catalog; an array is an explicit subset, including none (`[]`). */
export function useSaveAssistantSettings() {
  const qc = useQueryClient();
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: (body) => apiClient.put(`${BASE}/settings/`, body, { originPrincipal: principal }).then((r) => privateResult(r, principal)),
    onMutate: currentPrincipal,
    onSuccess: (_data, _body, context) => {
      if (!isCurrentPrincipal(context)) return;
      qc.invalidateQueries({ queryKey: assistantKeys.status });
      qc.invalidateQueries({ queryKey: assistantKeys.settings });
    },
  });
}

// ── Conversations ───────────────────────────────────────────────────────────

export function useConversations({ enabled = true } = {}) {
  return useQuery({
    queryKey: assistantKeys.conversations,
    queryFn: ({ signal }) => apiClient.get(`${BASE}/conversations/`, { signal }).then((r) => r.data.conversations),
    enabled,
    retry: retryTransient,
    staleTime: 30 * 1000,
    gcTime: ASSISTANT_CACHE_MS,
  });
}

export function useConversation(id) {
  const qc = useQueryClient();
  const principal = selectPrincipal(useAuthStore.getState());
  const navigation = useRef(null);
  const fetchPage = useCallback((pageParam, signal) => apiClient.get(`${BASE}/conversations/${seg(id)}/`, {
    params: { limit: HISTORY_PAGE_SIZE, ...pageParam }, signal, originPrincipal: principal,
  }).then((r) => r.data), [id, principal]);
  const query = useInfiniteQuery({
    queryKey: assistantKeys.conversation(id),
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
    initialPageParam: {},
    getPreviousPageParam: (first) => first.next_before ? { before: first.next_before } : undefined,
    getNextPageParam: (last) => last.next_after ? { after: last.next_after } : undefined,
    maxPages: HISTORY_MAX_PAGES,
    enabled: Boolean(id),
    retry: retryTransient,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    gcTime: ASSISTANT_CACHE_MS,
  });
  useEffect(() => {
    boundAssistantCache(qc);
    return () => navigation.current?.abort();
  }, [qc, id]);
  const replaceWindow = useCallback(async (pageParam) => {
    navigation.current?.abort();
    const controller = new AbortController();
    navigation.current = controller;
    await qc.cancelQueries({ queryKey: assistantKeys.conversation(id) });
    const page = await fetchPage(pageParam, controller.signal);
    if (controller.signal.aborted || principal !== selectPrincipal(useAuthStore.getState())) return undefined;
    qc.setQueryData(assistantKeys.conversation(id), { pages: [page], pageParams: [pageParam] });
    return page;
  }, [qc, id, fetchPage, principal]);
  const data = useMemo(() => flattenHistory(query.data), [query.data]);
  return {
    ...query,
    data,
    loadOlder: query.fetchPreviousPage,
    loadNewer: query.fetchNextPage,
    loadAround: useCallback((messageId) => replaceWindow({ around: messageId }), [replaceWindow]),
    loadLatest: useCallback(() => replaceWindow({}), [replaceWindow]),
    isLoadingOlder: query.isFetchingPreviousPage,
    isLoadingNewer: query.isFetchingNextPage,
  };
}

/** Run completion updates one light latest page, including dormant chats. */
export async function refreshConversationHistory(qc, id) {
  const principal = selectPrincipal(useAuthStore.getState());
  const controller = new AbortController();
  const stop = useAuthStore.subscribe((state) => {
    if (selectPrincipal(state) !== principal) controller.abort();
  });
  try {
    await qc.cancelQueries({ queryKey: assistantKeys.conversation(id) });
    const { data } = await apiClient.get(`${BASE}/conversations/${seg(id)}/`, {
      params: { limit: HISTORY_PAGE_SIZE }, signal: controller.signal, originPrincipal: principal,
    });
    if (controller.signal.aborted || principal !== selectPrincipal(useAuthStore.getState())) return;
    boundAssistantCache(qc);
    qc.setQueryData(assistantKeys.conversation(id), (existing) => mergeLatestHistory(existing, data));
  } finally { stop(); }
}

/** Canonical diagnostics are fetched only while their disclosure is open. */
export function useMessageDiagnostics(messageId, { enabled = false } = {}) {
  return useQuery({
    queryKey: assistantKeys.diagnostics(messageId),
    queryFn: ({ signal }) => apiClient.get(`${BASE}/messages/${seg(messageId)}/diagnostics/`, { signal }).then((r) => r.data),
    enabled: enabled && Boolean(messageId), retry: retryTransient,
    staleTime: Infinity, gcTime: 0, refetchOnWindowFocus: false,
  });
}

/** Collapsed dashboard revisions retain only their label until disclosure. */
export function useMessageDetails(messageId, { enabled = false } = {}) {
  const qc = useQueryClient();
  useEffect(() => boundAssistantCache(qc), [qc]);
  return useQuery({
    queryKey: assistantKeys.details(messageId),
    queryFn: ({ signal }) => apiClient.get(`${BASE}/messages/${seg(messageId)}/details/`, { signal }).then((r) => r.data),
    enabled: enabled && Boolean(messageId), retry: retryTransient,
    staleTime: Infinity, gcTime: ASSISTANT_CACHE_MS, refetchOnWindowFocus: false,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: ({ signal } = {}) => apiClient.post(`${BASE}/conversations/`, {}, { originPrincipal: principal, signal, timeout: RUN_REQUEST_TIMEOUT_MS }).then((r) => privateResult(r, principal)),
    onMutate: currentPrincipal,
    onSuccess: (_data, _body, context) => isCurrentPrincipal(context) && qc.invalidateQueries({ queryKey: assistantKeys.conversations }),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: (id) => apiClient.delete(`${BASE}/conversations/${seg(id)}/`, { originPrincipal: principal }).then((r) => privateResult(r, principal)),
    onMutate: currentPrincipal,
    onSuccess: (_data, id, context) => {
      if (!isCurrentPrincipal(context)) return;
      const cached = flattenHistory(qc.getQueryData(assistantKeys.conversation(id)));
      cached?.messages?.forEach((message) => {
        qc.removeQueries({ queryKey: ['assistant', 'widget', message.id] });
        qc.removeQueries({ queryKey: assistantKeys.details(message.id) });
        qc.removeQueries({ queryKey: assistantKeys.diagnostics(message.id) });
      });
      qc.removeQueries({ queryKey: assistantKeys.conversation(id) });
      qc.invalidateQueries({ queryKey: assistantKeys.conversations });
    },
  });
}

/** Rename only an owned chat; keep the open thread's messages in the cache. */
export function useRenameConversation() {
  const qc = useQueryClient();
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: ({ conversationId, title }) => apiClient.patch(`${BASE}/conversations/${seg(conversationId)}/`, { title }, { originPrincipal: principal }).then((r) => privateResult(r, principal)),
    onMutate: currentPrincipal,
    onSuccess: (item, { conversationId }, context) => {
      if (!isCurrentPrincipal(context)) return;
      qc.setQueryData(assistantKeys.conversations, (items) => items?.map((c) => c.id === conversationId ? { ...c, ...item } : c));
      qc.setQueryData(assistantKeys.conversation(conversationId), (previous) => previous?.pages
        ? { ...previous, pages: previous.pages.map((page) => ({ ...page, ...item })) }
        : previous ? { ...previous, ...item } : previous);
      qc.invalidateQueries({ queryKey: assistantKeys.conversations });
    },
  });
}

/** POST a question with the picked model and effort: resolves to `{message, run}`. */
export function useSendMessage() {
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: ({ conversationId, text, model, effort, skill, replyTo, quote, diagnostic, signal }) =>
      apiClient
        .post(`${BASE}/conversations/${seg(conversationId)}/messages/`, {
          text, model, effort,
          ...(skill !== undefined ? { skill } : {}),
          ...(replyTo ? { reply_to: replyTo } : {}),
          ...(quote ? { quote } : {}),
          ...(diagnostic ? { diagnostic: true } : {}),
        }, { originPrincipal: principal, signal, timeout: RUN_REQUEST_TIMEOUT_MS })
        .then((r) => privateResult(r, principal)),
  });
}

// ── Widgets ─────────────────────────────────────────────────────────────────

const isWidgetIndex = (index) => Number.isInteger(index) && index >= 0;

/** Re-run the query of an answer's widget number `index`: `{columns, rows}`. */
export function fetchWidgetData(messageId, index, filters, signal) {
  if (filters && Object.keys(filters).length) {
    return apiClient.post(`${BASE}/messages/${seg(messageId)}/widgets/${seg(index)}/`, { filters }, ...(signal ? [{ signal }] : [])).then((r) => r.data);
  }
  return apiClient.get(`${BASE}/messages/${seg(messageId)}/widgets/${seg(index)}/`, ...(signal ? [{ signal }] : [])).then((r) => r.data);
}

export const ACTIVE_RUN_POLL_MS = 2500;
export const RUN_REQUEST_TIMEOUT_MS = 10000;
let runObservation = 0;
// Sequence avoids clock changes and ties between accepting and checking a run.
export const markRunObservation = () => ++runObservation;

/** One active response per signed user and workspace, including other chats. */
export function useActiveRun({ enabled = true } = {}) {
  const principal = selectPrincipal(useAuthStore.getState());
  return useQuery({
    queryKey: assistantKeys.activeRun,
    queryFn: async ({ signal }) => {
      const checkSequence = markRunObservation();
      const response = await apiClient.get(`${BASE}/runs/active/`, { signal, originPrincipal: principal, timeout: RUN_REQUEST_TIMEOUT_MS });
      return { ...privateResult(response, principal), checkSequence };
    },
    enabled,
    retry: retryTransient,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: ACTIVE_RUN_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Cancellation is acknowledged by the server before another send is allowed. */
export function useCancelRun() {
  const principal = selectPrincipal(useAuthStore.getState());
  return useMutation({
    mutationFn: (runId) => apiClient.post(`${BASE}/runs/${seg(runId)}/cancel/`, {}, { originPrincipal: principal, timeout: RUN_REQUEST_TIMEOUT_MS })
      .then((response) => privateResult(response, principal)),
  });
}

export function useWidgetData(messageId, index, { enabled = true, filters } = {}) {
  const qc = useQueryClient();
  useEffect(() => boundAssistantCache(qc), [qc]);
  return useQuery({
    queryKey: assistantKeys.widget(messageId, index, filters),
    queryFn: ({ signal }) => fetchWidgetData(messageId, index, filters, signal),
    enabled: enabled && Boolean(messageId) && isWidgetIndex(index),
    retry: false,
    // Each fetch is a Cube query and a usage row: loaded once while shown,
    // reloaded only by the widget's «Повторить».
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    gcTime: ASSISTANT_CACHE_MS,
  });
}

// ── Run events ──────────────────────────────────────────────────────────────

export const RUN_POLL_MS = 700;
export const FINISHED_RUN_STATUSES = new Set(['done', 'failed', 'cancelled']);
// Consecutive transport failures tolerated before the poll gives up.
const MAX_POLL_FAILURES = 5;

export function fetchRunEvents(runId, after, signal, originPrincipal, timeout) {
  return apiClient
    .get(`${BASE}/runs/${seg(runId)}/events/`, { params: { after }, ...(signal ? { signal } : {}), ...(originPrincipal !== undefined ? { originPrincipal } : {}), ...(timeout ? { timeout } : {}) })
    .then((r) => r.data);
}

const STEP_KINDS = new Set(['note', 'action', 'result', 'error']);

/**
 * One «Ход мысли» step as `{kind, text}`, or null when it is malformed. An
 * unknown kind reads as an action.
 */
export function normalizeStep(step) {
  if (!step || typeof step.text !== 'string' || step.text === '') return null;
  return { kind: STEP_KINDS.has(step.kind) ? step.kind : 'action', text: step.text };
}

/**
 * Fold one batch of run events into the live view of a pending answer. Text
 * streamed before a step was the model's preamble to a tool call, which now
 * arrives as a note step — so a step starts the streamed text afresh, and the
 * answer is whatever streams after the last step. Each widget event is one
 * more widget of the answer (`widgets` counts them).
 */
export function foldRunEvents(state, events) {
  let { steps, text, widgets } = state;
  let diagnostics = state.diagnostics;
  for (const event of events || []) {
    const payload = event?.payload || {};
    const step = event?.kind === 'step' ? normalizeStep(payload) : null;
    if (step) {
      steps = [...steps, step];
      text = '';
    } else if (event?.kind === 'text' && typeof payload.delta === 'string') {
      text += payload.delta;
    } else if (event?.kind === 'widget') {
      widgets += 1;
    } else if (event?.kind === 'diagnostic' && payload && typeof payload === 'object' && !Array.isArray(payload) && typeof payload.kind === 'string') {
      diagnostics = [...(diagnostics || []), payload];
    }
  }
  return { ...state, steps, text, widgets, ...(diagnostics ? { diagnostics } : {}) };
}

/**
 * Poll one run's events every 700 ms, following the `next_after` cursor
 * (at once while the server says there are `more`).
 * `onBatch({status, events})` gets every successful page; `onFinish` is called
 * once — `{status, error_class}` when the run is done or failed, `{lost: true}`
 * on a 404 or after five transport failures in a row. Returns a cancel
 * function; nothing is called after it.
 */
export function pollRunEvents(runId, { onBatch, onFinish }) {
  let cancelled = false;
  let timer = null;
  let after = 0;
  let failures = 0;
  const controller = new AbortController();
  const principal = selectPrincipal(useAuthStore.getState());
  let unsubscribe = () => {};
  const cancel = () => {
    cancelled = true;
    controller.abort();
    clearTimeout(timer);
    unsubscribe();
  };
  unsubscribe = useAuthStore.subscribe((state) => {
    if (selectPrincipal(state) !== principal) cancel();
  });

  const tick = async () => {
    try {
      const data = await fetchRunEvents(runId, after, controller.signal, principal, RUN_REQUEST_TIMEOUT_MS);
      if (cancelled || principal !== selectPrincipal(useAuthStore.getState())) { cancel(); return; }
      failures = 0;
      after = Number.isInteger(data?.next_after) ? data.next_after : after;
      const status = data?.run?.status || 'queued';
      onBatch({ status, events: data?.events });
      if (cancelled) return;
      if (data?.more === true) {
        // A full page: fetch the rest now, and finish only once it is read.
        timer = setTimeout(tick, 0);
        return;
      }
      if (FINISHED_RUN_STATUSES.has(status)) {
        cancel();
        onFinish({ status, error_class: data?.run?.error_class || '' });
        return;
      }
    } catch (err) {
      if (cancelled) return;
      failures += 1;
      if (isAssistantNotFound(err) || failures >= MAX_POLL_FAILURES) {
        cancel();
        onFinish({ lost: true, notFound: isAssistantNotFound(err) });
        return;
      }
    }
    timer = setTimeout(tick, RUN_POLL_MS);
  };

  timer = setTimeout(tick, RUN_POLL_MS);
  return cancel;
}

const EMPTY_LIVE = { steps: [], text: '', widgets: 0, status: 'queued' };

/**
 * The live view of a pending answer, `{status, steps, text, widgets}`, polled
 * until the run ends; `onFinish` as in `pollRunEvents`.
 */
export function useRunEvents(runId, { onFinish, restartToken = 0 } = {}) {
  const [active, setActive] = useState({ runId: null, live: EMPTY_LIVE });
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  useEffect(() => {
    setActive({ runId, live: EMPTY_LIVE });
    if (!runId) return undefined;
    return pollRunEvents(runId, {
      onBatch: ({ status, events }) =>
        setActive((prev) => {
          // Empty polls carry only a cursor/status: preserve the live reference
          // so unchanged transcripts do not render while the model is thinking.
          if (prev.runId === runId && prev.live.status === status && (!events || events.length === 0)) return prev;
          return {
            runId,
            live: { ...foldRunEvents(prev.runId === runId ? prev.live : EMPTY_LIVE, events), status },
          };
        }),
      onFinish: (result) => finishRef.current?.(result, runId),
    });
  }, [runId, restartToken]);

  // A new run must never render the previous run's transcript before effects run.
  return active.runId === runId ? active.live : EMPTY_LIVE;
}
